import { useState } from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import {
  FaWhatsapp,
  FaEnvelope,
  FaMapMarkerAlt,
  FaClock,
  FaCheckCircle,
  FaExclamationCircle,
} from "react-icons/fa";
import { Loader2 } from "lucide-react";

// ⚠️ IMPORTANTE: Substitua pelo seu ID do Formspree
// 1. Acesse https://formspree.io e crie uma conta gratuita
// 2. Crie um novo form
// 3. Copie o ID e cole abaixo
const FORMSPREE_ID = "xrbwdqjk";

type FormStatus = "idle" | "submitting" | "success" | "error";

export function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [status, setStatus] = useState<FormStatus>("idle");
  const [errorMessage, setErrorMessage] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setStatus("submitting");
    setErrorMessage("");

    try {
      const response = await fetch(`https://formspree.io/f/${FORMSPREE_ID}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          Nome: formData.name,
          Email: formData.email,
          Telefone: formData.phone || "Não informado",
          Assunto: formData.subject,
          Mensagem: formData.message,
          _subject: `[Arcgel] ${formData.subject} - ${formData.name}`,
          _replyto: formData.email,
        }),
      });

      if (response.ok) {
        setStatus("success");
        setFormData({
          name: "",
          email: "",
          phone: "",
          subject: "",
          message: "",
        });
      } else {
        const data = await response.json();
        throw new Error(data.error || "Erro ao enviar mensagem");
      }
    } catch (error) {
      setStatus("error");
      setErrorMessage(
        error instanceof Error 
          ? error.message 
          : "Erro ao enviar mensagem. Tente novamente ou entre em contato pelo WhatsApp."
      );
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Limpa status de erro quando usuário começa a digitar
    if (status === "error") {
      setStatus("idle");
    }
  };

  const resetForm = () => {
    setStatus("idle");
    setErrorMessage("");
  };

  return (
    <>
      <Helmet>
        <title>Contato | Arcgel - Soluções em Climatização</title>
        <meta
          name="description"
          content="Entre em contato com a Arcgel. Solicite orçamento para instalação, manutenção e PMOC de ar condicionado. Atendimento em Santos e região."
        />
      </Helmet>

      <div className="container mx-auto px-4 py-20 md:py-32 pt-32">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Entre em{" "}
              <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                Contato
              </span>
            </h1>
            <p className="text-xl text-slate-300 max-w-2xl mx-auto">
              Estamos prontos para atender você. Solicite um orçamento ou tire suas dúvidas.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Informações de Contato */}
            <div className="space-y-8">
              <h2 className="text-2xl font-semibold mb-6 text-white">
                Informações de Contato
              </h2>

              <a
                href="https://wa.me/5513991028221"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-4 p-4 rounded-lg hover:bg-white/5 transition-colors group"
              >
                <FaWhatsapp className="text-green-500 text-2xl group-hover:scale-110 transition-transform" />
                <div>
                  <h3 className="font-medium text-white">WhatsApp</h3>
                  <p className="text-slate-300">+55 13 99102-8221</p>
                  <span className="text-xs text-green-400">Clique para conversar</span>
                </div>
              </a>

              <div className="flex items-center space-x-4 p-4">
                <FaEnvelope className="text-cyan-400 text-2xl" />
                <div>
                  <h3 className="font-medium text-white">Email</h3>
                  <p className="text-slate-300">contato@arcgel.com.br</p>
                </div>
              </div>

              <div className="flex items-center space-x-4 p-4">
                <FaMapMarkerAlt className="text-blue-400 text-2xl" />
                <div>
                  <h3 className="font-medium text-white">Endereço</h3>
                  <p className="text-slate-300">Av. Principal, 1000 - Santos/SP</p>
                </div>
              </div>

              <div className="flex items-center space-x-4 p-4">
                <FaClock className="text-yellow-400 text-2xl" />
                <div>
                  <h3 className="font-medium text-white">Horário</h3>
                  <p className="text-slate-300">Seg a Sex: 8h às 18h | Emergências 24h</p>
                </div>
              </div>

              {/* CTA WhatsApp */}
              <a
                href="https://wa.me/5513991028221?text=Olá! Gostaria de solicitar um orçamento."
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-3 w-full p-4 bg-green-600 hover:bg-green-500 text-white font-semibold rounded-lg transition-all duration-300 mt-8"
              >
                <FaWhatsapp className="text-xl" />
                Chamar no WhatsApp
              </a>
            </div>

            {/* Formulário de Contato */}
            <div>
              {status === "success" ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex flex-col items-center justify-center h-full text-center p-8 rounded-2xl bg-green-500/10 border border-green-500/30"
                >
                  <FaCheckCircle className="text-green-500 text-6xl mb-4" />
                  <h3 className="text-2xl font-bold text-white mb-2">
                    Mensagem Enviada!
                  </h3>
                  <p className="text-slate-300 mb-6">
                    Recebemos sua mensagem e entraremos em contato em breve.
                  </p>
                  <button
                    onClick={resetForm}
                    className="px-6 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
                  >
                    Enviar nova mensagem
                  </button>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {status === "error" && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex items-center gap-3 p-4 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400"
                    >
                      <FaExclamationCircle className="flex-shrink-0" />
                      <span className="text-sm">{errorMessage}</span>
                    </motion.div>
                  )}

                  <div>
                    <label
                      htmlFor="name"
                      className="block text-sm font-medium text-white mb-2"
                    >
                      Nome *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      disabled={status === "submitting"}
                      placeholder="Digite seu nome"
                      className="block w-full rounded-lg bg-slate-800/50 border border-slate-600 text-white placeholder-slate-500 shadow-sm focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 p-3 disabled:opacity-50"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-white mb-2"
                    >
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      disabled={status === "submitting"}
                      placeholder="Digite seu email"
                      className="block w-full rounded-lg bg-slate-800/50 border border-slate-600 text-white placeholder-slate-500 shadow-sm focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 p-3 disabled:opacity-50"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="phone"
                      className="block text-sm font-medium text-white mb-2"
                    >
                      Telefone
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      disabled={status === "submitting"}
                      placeholder="(00) 00000-0000"
                      className="block w-full rounded-lg bg-slate-800/50 border border-slate-600 text-white placeholder-slate-500 shadow-sm focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 p-3 disabled:opacity-50"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="subject"
                      className="block text-sm font-medium text-white mb-2"
                    >
                      Assunto *
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      disabled={status === "submitting"}
                      className="block w-full rounded-lg bg-slate-800/50 border border-slate-600 text-white shadow-sm focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 p-3 disabled:opacity-50"
                    >
                      <option value="">Selecione um assunto</option>
                      <option value="Orçamento">Orçamento</option>
                      <option value="Manutenção">Manutenção</option>
                      <option value="Instalação">Instalação</option>
                      <option value="PMOC">PMOC</option>
                      <option value="Outro">Outro</option>
                    </select>
                  </div>

                  <div>
                    <label
                      htmlFor="message"
                      className="block text-sm font-medium text-white mb-2"
                    >
                      Mensagem *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      disabled={status === "submitting"}
                      rows={4}
                      placeholder="Digite sua mensagem"
                      className="block w-full rounded-lg bg-slate-800/50 border border-slate-600 text-white placeholder-slate-500 shadow-sm focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 p-3 disabled:opacity-50 resize-none"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white py-3 px-4 rounded-lg hover:from-cyan-600 hover:to-blue-700 transition-all duration-300 font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {status === "submitting" ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Enviando...
                      </>
                    ) : (
                      "Enviar Mensagem"
                    )}
                  </button>

                  <p className="text-xs text-slate-500 text-center">
                    * Campos obrigatórios
                  </p>
                </form>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </>
  );
}
